import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { checkInDayKey, checkInDayWindowForDay, checkInWeekday, shiftCheckInDay } from "@/lib/checkInDates";

/* ── Types ────────────────────────────────────────── */
export type ChallengeStatus = "active" | "completed" | "failed" | "expired";

export interface ChallengeCheckpoint {
  day: number;
  completed: boolean;
  completedAt?: string;
}

export interface Challenge {
  id: string;
  weekNumber: number;
  title: string;
  description: string;
  category: string;
  targetType: string;
  targetValue: number;
  currentProgress: number;
  progressUnit: string;
  startDate: string;
  endDate: string;
  status: ChallengeStatus;
  rewardPoints: number;
  rewardBadgeId: string | null;
  reasoning: string;
  checkpoints: ChallengeCheckpoint[];
}

export interface ChallengeBadge {
  id: string;
  badgeKey: string;
  title: string;
  emoji: string;
  unlocked: boolean;
  unlockedAt?: string | null;
  relatedWeek?: number | null;
}

/* ── Default badge definitions (seeded per user on first load) ── */
/* Badge titles use i18n keys: badge.{badgeKey} — resolved at render time */
const DEFAULT_BADGE_DEFS: Omit<ChallengeBadge, "id">[] = [
  { badgeKey: "consistent-athlete", title: "Consistent Athlete", emoji: "💪", unlocked: false },
  { badgeKey: "self-aware", title: "Self-Aware", emoji: "📊", unlocked: false },
  { badgeKey: "deep-worker", title: "Deep Worker", emoji: "🎯", unlocked: false },
  { badgeKey: "sleep-champion", title: "Sleep Champion", emoji: "😴", unlocked: false },
  { badgeKey: "clean-fuel", title: "Clean Fuel", emoji: "🥗", unlocked: false },
  { badgeKey: "mind-master", title: "Mind Master", emoji: "🧘", unlocked: false },
  { badgeKey: "iron-will", title: "Iron Will", emoji: "🔥", unlocked: false },
  { badgeKey: "social-anchor", title: "Social Anchor", emoji: "🤝", unlocked: false },
  { badgeKey: "knowledge-seeker", title: "Knowledge Seeker", emoji: "📚", unlocked: false },
  { badgeKey: "early-riser", title: "Early Riser", emoji: "🌅", unlocked: false },
  { badgeKey: "zen-master", title: "Zen Master", emoji: "🧠", unlocked: false },
  { badgeKey: "unstoppable", title: "Unstoppable", emoji: "⚡", unlocked: false },
];

/* ── Challenge template definitions ───────────────── */
interface ChallengeTemplate {
  category: string;
  targetType: string;
  targetValue: number;
  progressUnit: string;
  rewardPoints: number;
  rewardBadgeId: string;
  /** i18n key suffix: challenge.tpl.{key}.title / .description / .reasoning */
  i18nKey: string;
}

const CHALLENGE_TEMPLATES: ChallengeTemplate[] = [
  {
    category: "consistency",
    targetType: "days",
    targetValue: 5,
    progressUnit: "days",
    rewardPoints: 10,
    rewardBadgeId: "self-aware",
    i18nKey: "checkin_streak",
  },
  {
    category: "discipline",
    targetType: "days",
    targetValue: 5,
    progressUnit: "days",
    rewardPoints: 10,
    rewardBadgeId: "iron-will",
    i18nKey: "discipline_above",
  },
  {
    category: "energy",
    targetType: "days",
    targetValue: 5,
    progressUnit: "days",
    rewardPoints: 8,
    rewardBadgeId: "consistent-athlete",
    i18nKey: "energy_above",
  },
  {
    category: "confidence",
    targetType: "days",
    targetValue: 4,
    progressUnit: "days",
    rewardPoints: 8,
    rewardBadgeId: "zen-master",
    i18nKey: "confidence_above",
  },
  {
    category: "reflection",
    targetType: "days",
    targetValue: 4,
    progressUnit: "days",
    rewardPoints: 8,
    rewardBadgeId: "mind-master",
    i18nKey: "reflection_streak",
  },
];

/* ── Row → domain model ──────────────────────────── */
function rowToChallenge(r: any): Challenge {
  return {
    id: r.id,
    weekNumber: r.week_number,
    title: r.title,
    description: r.description,
    category: r.category,
    targetType: r.target_type,
    targetValue: r.target_value,
    currentProgress: r.current_progress,
    progressUnit: r.progress_unit,
    startDate: r.start_date,
    endDate: r.end_date,
    status: r.status as ChallengeStatus,
    rewardPoints: r.reward_points,
    rewardBadgeId: r.reward_badge_id,
    reasoning: r.reasoning,
    checkpoints: (r.checkpoints ?? []) as ChallengeCheckpoint[],
  };
}

function rowToBadge(r: any): ChallengeBadge {
  return {
    id: r.id,
    badgeKey: r.badge_key,
    title: r.title,
    emoji: r.emoji,
    unlocked: r.unlocked,
    unlockedAt: r.unlocked_at,
    relatedWeek: r.related_week,
  };
}

/* ── Helpers ──────────────────────────────────────── */
function getWeekNumber(): number {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  return Math.ceil(((now.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7);
}

function getWeekBounds(): { start: Date; end: Date } {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const start = new Date(now);
  start.setDate(now.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/** Check-in occurrence week is SP; challenge scheduling keeps its existing basis. */
function checkinsThisWeek<T extends { checked_in_at: string | null }>(rows: T[]): T[] {
  const now = new Date();
  const today = checkInDayKey(now)!;
  const weekday = checkInWeekday(today);
  const monday = shiftCheckInDay(today, weekday === 0 ? -6 : 1 - weekday);
  const start = Date.parse(checkInDayWindowForDay(monday).start);
  return rows.filter(row => row.checked_in_at !== null && Date.parse(row.checked_in_at) >= start && Date.parse(row.checked_in_at) <= now.getTime());
}

/** Pick the best challenge template based on recent check-in weaknesses */
function pickTemplate(
  checkins: Array<{ mood: number | null; energy: number | null; note: string | null }>,
  pastCategories: string[],
  disciplineScore: number | null,
  confidenceScore: number | null,
): ChallengeTemplate | null {
  if (checkins.length === 0) {
    // No check-in data yet → consistency challenge
    return CHALLENGE_TEMPLATES[0];
  }

  // Calculate averages from recent check-ins
  const avg = (vals: (number | null)[]) => {
    const valid = vals.filter((v): v is number => v !== null && v !== undefined);
    return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : 2;
  };

  const energyAvg = avg(checkins.map(c => c.energy));
  const reflectionRate = checkins.filter(c => c.note && c.note.trim().length > 0).length / checkins.length;

  // Score each template (lower = more needed)
  // Filter out discipline/confidence templates when their score is null
  const scores: Array<{ tpl: ChallengeTemplate; score: number }> = [
    { tpl: CHALLENGE_TEMPLATES[0], score: checkins.length >= 5 ? 3 : 0 }, // consistency
    { tpl: CHALLENGE_TEMPLATES[2], score: energyAvg }, // energy
    { tpl: CHALLENGE_TEMPLATES[4], score: reflectionRate * 4 }, // reflection
  ];

  if (disciplineScore !== null) {
    scores.push({ tpl: CHALLENGE_TEMPLATES[1], score: disciplineScore }); // discipline
  }
  if (confidenceScore !== null) {
    scores.push({ tpl: CHALLENGE_TEMPLATES[3], score: confidenceScore }); // confidence
  }

  if (scores.length === 0) return null;

  // Deprioritize recently-used categories
  const filtered = scores.filter(s => !pastCategories.includes(s.tpl.category));
  const pool = filtered.length > 0 ? filtered : scores;

  // Pick the weakest area
  pool.sort((a, b) => a.score - b.score);
  return pool[0].tpl;
}

/** Build a dynamic reasoning string using an i18n function */
function buildReasoning(
  t: (key: string) => string,
  template: ChallengeTemplate,
  checkins: Array<{ energy: number | null; note: string | null }>,
): string {
  // Try template-specific reasoning first
  const key = `challenge.tpl.${template.i18nKey}.reasoning`;
  const translated = t(key);
  if (translated !== key) return translated;

  // Fallback to generic
  return t("challenge.insightGeneric");
}

/* ── Hook ─────────────────────────────────────────── */
export function useChallenges(tFunc?: (key: string) => string) {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [badges, setBadges] = useState<ChallengeBadge[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Fallback t function that returns the key
  const t = tFunc ?? ((k: string) => k);

  // Restore auth session before running any challenge queries/inserts
  useEffect(() => {
    let mounted = true;

    const restoreSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      setUserId(session?.user?.id ?? null);
      setAuthReady(true);
    };

    restoreSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setUserId(session?.user?.id ?? null);
      setAuthReady(true);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Fetch data when auth session is ready
  useEffect(() => {
    if (!authReady) return;
    if (!userId) {
      setChallenges([]);
      setBadges([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {

      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      const [chRes, bgRes, ciRes, habitsRes, habitCompRes, goalProgressRes] = await Promise.all([
        supabase.from("challenges").select("*").eq("user_id", userId).order("week_number", { ascending: false }),
        supabase.from("challenge_badges").select("*").eq("user_id", userId).order("created_at", { ascending: true }),
        supabase.from("daily_checkins").select("*").eq("user_id", userId)
          .not("checked_in_at", "is", null).lte("checked_in_at", new Date().toISOString())
          .order("checked_in_at", { ascending: false }).limit(14),
        supabase.from("habits").select("name, is_active").eq("user_id", userId).eq("is_active", true).limit(3),
        supabase.from("habit_completions").select("id").eq("user_id", userId).gte("completed_date", weekAgo),
        supabase.from("goals").select("progress_override").eq("user_id", userId).eq("is_completed", false),
      ]);

      if (cancelled) return;

      if (chRes.error) { console.error(chRes.error); setLoading(false); return; }
      if (bgRes.error) { console.error(bgRes.error); setLoading(false); return; }
      if (ciRes.error) { console.error(ciRes.error); setLoading(false); return; }

      // Discipline score: completions this week / (active habits * 7) * 100
      const activeHabitCount = (habitsRes.data || []).length;
      const completionsThisWeek = (habitCompRes.data || []).length;
      const expectedCompletions = activeHabitCount * 7;
      const disciplineScore = expectedCompletions > 0
        ? Math.round((completionsThisWeek / expectedCompletions) * 100)
        : null;

      // Confidence score: average progress % on active goals
      const goalsWithProgress = (goalProgressRes.data || []).filter((g: any) => g.progress_override != null);
      const confidenceScore = goalsWithProgress.length > 0
        ? Math.round(goalsWithProgress.reduce((sum: number, g: any) => sum + (g.progress_override || 0), 0) / goalsWithProgress.length)
        : null;

      let challengeRows = (chRes.data ?? []).map(rowToChallenge);
      const recentCheckins = ciRes.data ?? [];

      let badgeRows = bgRes.data ?? [];
      if (badgeRows.length === 0) {
        const inserts = DEFAULT_BADGE_DEFS.map(b => ({
          user_id: userId,
          badge_key: b.badgeKey,
          title: b.title,
          emoji: b.emoji,
          unlocked: false,
        }));
        const { data: inserted, error } = await supabase
          .from("challenge_badges")
          .insert(inserts as any)
          .select();
        if (!error && inserted) {
          badgeRows = inserted;
        } else {
          console.warn("Badge seeding failed, using local fallback:", error?.message);
          badgeRows = DEFAULT_BADGE_DEFS.map((b, i) => ({
            id: `fallback-${i}`,
            user_id: userId,
            badge_key: b.badgeKey,
            title: b.title,
            emoji: b.emoji,
            unlocked: false,
            unlocked_at: null as any,
            related_week: null as any,
            created_at: new Date().toISOString(),
          }));
        }
      }

      const now = new Date();
      for (const ch of challengeRows) {
        if (ch.status === "active" && new Date(ch.endDate) < now) {
          const finalStatus: ChallengeStatus = ch.currentProgress >= ch.targetValue ? "completed" : "expired";
          await supabase.from("challenges").update({ status: finalStatus } as any).eq("id", ch.id);
          ch.status = finalStatus;

          if (finalStatus === "completed" && ch.rewardBadgeId) {
            await supabase.from("challenge_badges")
              .update({ unlocked: true, unlocked_at: now.toISOString(), related_week: ch.weekNumber } as any)
              .eq("user_id", userId)
              .eq("badge_key", ch.rewardBadgeId);
          }
        }
      }

      const activeChallenge = challengeRows.find(c => c.status === "active");
      if (activeChallenge) {
        const weekCheckins = checkinsThisWeek(recentCheckins);
        const updatedCheckpoints = syncCheckpointsFromCheckins(activeChallenge, weekCheckins);

        if (updatedCheckpoints) {
          const newProgress = updatedCheckpoints.filter(cp => cp.completed).length;
          const isComplete = newProgress >= activeChallenge.targetValue;
          const newStatus: ChallengeStatus = isComplete ? "completed" : "active";

          await supabase.from("challenges").update({
            checkpoints: updatedCheckpoints as any,
            current_progress: newProgress,
            status: newStatus,
          } as any).eq("id", activeChallenge.id);

          activeChallenge.checkpoints = updatedCheckpoints;
          activeChallenge.currentProgress = newProgress;
          activeChallenge.status = newStatus;

          if (isComplete && activeChallenge.rewardBadgeId) {
            await supabase.from("challenge_badges")
              .update({ unlocked: true, unlocked_at: now.toISOString(), related_week: activeChallenge.weekNumber } as any)
              .eq("user_id", userId)
              .eq("badge_key", activeChallenge.rewardBadgeId);
          }
        }
      }

      const hasActive = challengeRows.some(c => c.status === "active");
      if (!hasActive && recentCheckins.length > 0) {
        const pastCategories = challengeRows
          .filter(c => c.status !== "active")
          .slice(0, 3)
          .map(c => c.category);

        const template = pickTemplate(recentCheckins as any, pastCategories, disciplineScore, confidenceScore);

        if (!template) {
          // All applicable templates were filtered out (null scores) — show onboarding message
          const weekNum = getWeekNumber();
          const { start, end } = getWeekBounds();
          const onboardingChallenge: Challenge = {
            id: "onboarding-placeholder",
            weekNumber: weekNum,
            title: t("challenge.tpl.checkin_streak.title") !== "challenge.tpl.checkin_streak.title"
              ? t("challenge.tpl.checkin_streak.title")
              : "Complete your first habit this week to unlock challenges",
            description: "",
            category: "consistency",
            targetType: "days",
            targetValue: 5,
            currentProgress: 0,
            progressUnit: "days",
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            status: "active",
            rewardPoints: 0,
            rewardBadgeId: null,
            reasoning: "",
            checkpoints: Array.from({ length: 5 }, (_, i) => ({ day: i + 1, completed: false })),
          };
          challengeRows = [onboardingChallenge, ...challengeRows];
        } else {

        const weekNum = getWeekNumber();
        const { start, end } = getWeekBounds();

        const checkpoints: ChallengeCheckpoint[] = Array.from({ length: template.targetValue }, (_, i) => ({
          day: i + 1,
          completed: false,
        }));

        const title = t(`challenge.tpl.${template.i18nKey}.title`);
        const description = t(`challenge.tpl.${template.i18nKey}.description`);
        const reasoning = buildReasoning(t, template, recentCheckins as any);

        const insert = {
          user_id: userId,
          week_number: weekNum,
          title: title !== `challenge.tpl.${template.i18nKey}.title` ? title : template.i18nKey,
          description: description !== `challenge.tpl.${template.i18nKey}.description` ? description : "",
          category: template.category,
          target_type: template.targetType,
          target_value: template.targetValue,
          current_progress: 0,
          progress_unit: template.progressUnit,
          start_date: start.toISOString(),
          end_date: end.toISOString(),
          status: "active",
          reward_points: template.rewardPoints,
          reward_badge_id: template.rewardBadgeId,
          reasoning: reasoning !== "challenge.insightGeneric" ? reasoning : "",
          checkpoints: checkpoints as any,
        };

        const { data: newCh, error } = await supabase
          .from("challenges")
          .insert(insert as any)
          .select()
          .single();

        if (!error && newCh && !cancelled) {
          challengeRows = [rowToChallenge(newCh), ...challengeRows];

          const weekCheckins = checkinsThisWeek(recentCheckins);
          const synced = syncCheckpointsFromCheckins(rowToChallenge(newCh), weekCheckins);
          if (synced) {
            const prog = synced.filter(cp => cp.completed).length;
            const complete = prog >= template.targetValue;
            await supabase.from("challenges").update({
              checkpoints: synced as any,
              current_progress: prog,
              status: complete ? "completed" : "active",
            } as any).eq("id", newCh.id);
            challengeRows[0] = { ...challengeRows[0], checkpoints: synced, currentProgress: prog, status: complete ? "completed" : "active" };
          }
        } else if (!cancelled) {
          console.error("Challenge auto-generation failed:", error?.message, error?.details);
          const fallbackChallenge: Challenge = {
            id: "local-first-challenge",
            weekNumber: weekNum,
            title: title !== `challenge.tpl.${template.i18nKey}.title` ? title : "Complete 5 Daily Check-Ins",
            description: description !== `challenge.tpl.${template.i18nKey}.description` ? description : "Build your self-awareness habit.",
            category: template.category,
            targetType: template.targetType,
            targetValue: template.targetValue,
            currentProgress: 0,
            progressUnit: template.progressUnit,
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            status: "active",
            rewardPoints: template.rewardPoints,
            rewardBadgeId: template.rewardBadgeId,
            reasoning: reasoning !== "challenge.insightGeneric" ? reasoning : t("challenge.insightGeneric"),
            checkpoints,
          };

          const syncedFallback = syncCheckpointsFromCheckins(fallbackChallenge, checkinsThisWeek(recentCheckins));
          challengeRows = [{
            ...fallbackChallenge,
            checkpoints: syncedFallback ?? fallbackChallenge.checkpoints,
            currentProgress: (syncedFallback ?? fallbackChallenge.checkpoints).filter(cp => cp.completed).length,
          }];
        }

        } // end: template !== null
      }

      if (!cancelled) {
        setChallenges(challengeRows);
        setBadges(badgeRows.map(rowToBadge));
        setLoading(false);
      }

      } catch (e) {
        console.error("[useChallenges] load error:", e);
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, userId, refreshKey]);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  const activeChallenge = challenges.find(c => c.status === "active") ?? null;
  const pastChallenges = challenges.filter(c => c.status !== "active");

  const logDay = useCallback(async (challengeId: string, dayIndex: number) => {
    const ch = challenges.find(c => c.id === challengeId);
    if (!ch) return;
    // Guard: already completed by auto-sync — skip silently
    if (ch.checkpoints[dayIndex]?.completed) return;
    // Guard: cannot log future days beyond current progress + 1
    const nextIncomplete = ch.checkpoints.findIndex(cp => !cp.completed);
    if (nextIncomplete === -1 || dayIndex > nextIncomplete) return;

    const newCheckpoints = ch.checkpoints.map((cp, i) =>
      i === dayIndex ? { ...cp, completed: true, completedAt: new Date().toISOString() } : cp
    );
    const newProgress = newCheckpoints.filter(cp => cp.completed).length;
    const isComplete = newProgress >= ch.targetValue;
    const newStatus: ChallengeStatus = isComplete ? "completed" : ch.status;

    // Optimistic update
    setChallenges(prev => prev.map(c =>
      c.id === challengeId
        ? { ...c, checkpoints: newCheckpoints, currentProgress: newProgress, status: newStatus }
        : c
    ));

    const { error } = await supabase
      .from("challenges")
      .update({
        checkpoints: newCheckpoints as any,
        current_progress: newProgress,
        status: newStatus,
      } as any)
      .eq("id", challengeId);

    if (error) {
      console.error(error);
      setChallenges(prev => prev.map(c => c.id === challengeId ? ch : c));
      return;
    }

    // Unlock badge if completed
    if (isComplete && ch.rewardBadgeId) {
      const { error: bErr } = await supabase
        .from("challenge_badges")
        .update({
          unlocked: true,
          unlocked_at: new Date().toISOString(),
          related_week: ch.weekNumber,
        } as any)
        .eq("user_id", userId!)
        .eq("badge_key", ch.rewardBadgeId);

      if (!bErr) {
        setBadges(prev => prev.map(b =>
          b.badgeKey === ch.rewardBadgeId
            ? { ...b, unlocked: true, unlockedAt: new Date().toISOString(), relatedWeek: ch.weekNumber }
            : b
        ));
      }
    }
  }, [challenges, userId]);

  const completeChallenge = useCallback(async (challengeId: string) => {
    const ch = challenges.find(c => c.id === challengeId);
    if (!ch) return;

    setChallenges(prev => prev.map(c =>
      c.id === challengeId ? { ...c, status: "completed" as ChallengeStatus, currentProgress: c.targetValue } : c
    ));

    await supabase.from("challenges").update({ status: "completed", current_progress: ch.targetValue } as any).eq("id", challengeId);

    if (ch.rewardBadgeId) {
      await supabase.from("challenge_badges")
        .update({ unlocked: true, unlocked_at: new Date().toISOString(), related_week: ch.weekNumber } as any)
        .eq("user_id", userId!)
        .eq("badge_key", ch.rewardBadgeId);

      setBadges(prev => prev.map(b =>
        b.badgeKey === ch.rewardBadgeId ? { ...b, unlocked: true, unlockedAt: new Date().toISOString(), relatedWeek: ch.weekNumber } : b
      ));
    }
  }, [challenges, userId]);

  const failChallenge = useCallback(async (challengeId: string) => {
    setChallenges(prev => prev.map(c =>
      c.id === challengeId ? { ...c, status: "failed" as ChallengeStatus } : c
    ));
    await supabase.from("challenges").update({ status: "failed" } as any).eq("id", challengeId);
  }, []);

  const getDaysRemaining = useCallback((challenge: Challenge) => {
    const end = new Date(challenge.endDate);
    const now = new Date();
    return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / 86400000));
  }, []);

  return {
    activeChallenge,
    pastChallenges,
    badges,
    loading,
    logDay,
    completeChallenge,
    failChallenge,
    getDaysRemaining,
    refresh,
  };
}

/* ── Checkpoint sync logic ────────────────────────── */
function syncCheckpointsFromCheckins(
  challenge: Challenge,
  weekCheckins: Array<{
    checked_in_at: string | null;
    mood: number | null;
    energy: number | null;
    discipline: number | null;
    confidence: number | null;
    note: string | null;
  }>,
): ChallengeCheckpoint[] | null {
  // Unknown occurrence timestamps do not acquire a day from their creation time.
  const byDate = new Map<string, typeof weekCheckins[0]>();
  for (const ci of weekCheckins) {
    const dateKey = ci.checked_in_at ? checkInDayKey(ci.checked_in_at) : null;
    if (!dateKey) continue;
    if (!byDate.has(dateKey)) byDate.set(dateKey, ci);
  }

  const sortedDates = Array.from(byDate.keys()).sort();
  let changed = false;
  const newCheckpoints = [...challenge.checkpoints];

  for (let i = 0; i < newCheckpoints.length; i++) {
    if (newCheckpoints[i].completed) continue;
    if (i >= sortedDates.length) break;

    const ci = byDate.get(sortedDates[i]);
    if (!ci) continue;

    let passes = false;

    switch (challenge.category) {
      case "consistency":
        // Any check-in counts
        passes = true;
        break;
      case "discipline":
        passes = (ci.discipline ?? 0) >= 3;
        break;
      case "energy":
        passes = (ci.energy ?? 0) >= 3;
        break;
      case "confidence":
        passes = (ci.confidence ?? 0) >= 3;
        break;
      case "reflection":
        passes = !!(ci.note && ci.note.trim().length > 0);
        break;
      default:
        passes = true;
    }

    if (passes) {
      newCheckpoints[i] = { ...newCheckpoints[i], completed: true, completedAt: ci.checked_in_at! };
      changed = true;
    }
  }

  return changed ? newCheckpoints : null;
}
